import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, GoldButton, Input } from "@/components/ui-kit";

export const GoogleButton = ({ testid = "google-login-button" }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/portal";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
    className="w-full rounded-full border border-white/15 bg-white/[0.03] text-slate-200 px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:border-white/40 hover:-translate-y-0.5 flex items-center justify-center gap-3"
  >
    <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.75 12 .75 7.62.75 3.84 3.27 2 6.98l3.66 2.84C6.54 7.04 9.02 5.04 12 5.04z"/><path fill="#4285F4" d="M23.25 12.26c0-.92-.08-1.6-.26-2.31H12v4.19h6.44c-.13 1.08-.83 2.7-2.39 3.79l3.57 2.77c2.14-1.97 3.63-4.88 3.63-8.44z"/><path fill="#FBBC05" d="M5.66 14.18a7.2 7.2 0 0 1 0-4.36L2 6.98a11.25 11.25 0 0 0 0 10.04l3.66-2.84z"/><path fill="#34A853" d="M12 23.25c3.04 0 5.6-1 7.46-2.72l-3.57-2.77c-.95.66-2.24 1.12-3.89 1.12-2.98 0-5.46-2-6.34-4.7L2 17.02c1.84 3.71 5.62 6.23 10 6.23z"/></svg>
    Continue with Google
  </button>
);

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      localStorage.setItem("cc_token", res.data.token);
      setUser(res.data.user);
      const u = res.data.user;
      toast.success(`Welcome back${u.first_name ? `, ${u.first_name}` : ""}!`);
      if (u.role === "admin") navigate("/admin", { replace: true });
      else if (!u.onboarding_completed) navigate("/onboarding", { replace: true });
      else navigate(location.state?.from || "/portal", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060D1F] flex flex-col" data-testid="login-page">
      <div className="px-5 sm:px-8 pt-6"><Logo /></div>
      <main className="flex-1 flex items-center justify-center px-5 py-14">
        <div className="w-full max-w-md fade-up">
          <h1 className="text-3xl sm:text-4xl text-white tracking-tight">Welcome back</h1>
          <p className="mt-3 text-slate-400">Your community missed you.</p>
          <form onSubmit={submit} className="mt-9 space-y-5" data-testid="login-form">
            <Input data-testid="login-email-input" label="Email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <Input data-testid="login-password-input" label="Password" type="password" placeholder="Your password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            <GoldButton data-testid="login-submit-button" type="submit" disabled={loading} className="w-full">
              {loading ? "Logging in..." : "Log in"}
            </GoldButton>
          </form>
          <div className="my-6 flex items-center gap-4">
            <div className="h-px flex-1 bg-white/10" /><span className="text-xs text-slate-500 uppercase tracking-widest">or</span><div className="h-px flex-1 bg-white/10" />
          </div>
          <GoogleButton />
          <div className="mt-7 flex items-center justify-between text-sm">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-slate-400 transition-colors duration-300 hover:text-[#D4AF37]">Forgot password?</Link>
            <Link to="/onboarding" data-testid="join-link" className="text-[#D4AF37]">Become a Free Member</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
